/**
 * TrainingModulePlayer.jsx
 * ========================
 * Lecteur de module de formation data-driven.
 * Charge un module JSON (cf. /modules-data) et le rend via OnboardingSlideshow + SlideRenderer.
 *
 * Usage :
 *   <TrainingModulePlayer module={moduleData} onClose={() => navigate(-1)} />
 */
import React, { useMemo, useEffect } from 'react';
import OnboardingSlideshow from '../docs/OnboardingSlideshow';
import SlideRenderer from './SlideRenderer';
import { markSlideViewed } from './progressService';

// Thèmes par population
const THEMES = {
  client: {
    gradient: 'from-emerald-50 via-white to-teal-50',
    accent: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    noteBorder: 'border-emerald-500',
    border: 'border-emerald-100',
    ctaBg: 'bg-emerald-600',
  },
  staff: {
    gradient: 'from-slate-50 via-white to-indigo-50',
    accent: 'text-indigo-600',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    noteBorder: 'border-indigo-500',
    border: 'border-indigo-100',
    ctaBg: 'bg-indigo-600',
  },
};

const TrainingModulePlayer = ({ module: mod, onClose, pdfDownloadUrl }) => {
  const theme = THEMES[mod?.population] || THEMES.client;

  // Convertit chaque slide JSON en composant React (signature attendue par OnboardingSlideshow)
  const slides = useMemo(
    () =>
      (mod?.slides || []).map((slide, idx) => ({
        id: slide.id || `slide-${idx}`,
        title: slide.title || `Slide ${idx + 1}`,
        Component: () => <SlideRenderer slide={slide} theme={theme} />,
      })),
    [mod, theme]
  );

  if (!mod || slides.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Module de formation indisponible.</p>
      </div>
    );
  }

  // Tracking automatique de la progression dans localStorage
  const handleSlideChange = (idx, total) => {
    markSlideViewed(mod.id, idx, total);
  };

  return (
    <OnboardingSlideshow
      slides={slides}
      onClose={onClose}
      pdfDownloadUrl={pdfDownloadUrl || mod.pdfUrl}
      onSlideChange={handleSlideChange}
    />
  );
};

export default TrainingModulePlayer;
